// Puts the things that can pop up when a reader arrives in a sensible order, so they do not all
// land at once: the preference onboarding, Intercom's messages and the NPS survey.
//
//   1. The preference onboarding comes first, when it is due. Intercom starts after it closes.
//   2. When the onboarding is not shown, Intercom starts straight away, and we then wait a few
//      seconds for it to say it has a message. Only if it has none is the NPS survey loaded.
//   3. When the onboarding was shown, the NPS survey is not loaded in this visit, nor in any later
//      page load of the same browser session (the web reloads the page between screens, and the
//      state below does not survive that, so the fact is also kept in sessionStorage).
//
// Intercom and SatisMeter decide for themselves what to show once they are running, so all we can
// do is choose when they start. The state lives for one page load, and starts afresh whenever the
// reader logs in or out without the page reloading (the apps do this), because a decision made for
// a logged-out visitor says nothing about the reader who logs in next.
//
// Nothing here may leave the reader without support or the app unusable: every hold is time-limited,
// and if the onboarding never reports, Intercom and the survey carry on after DECISION_TIMEOUT_MS.

// How long Intercom is held waiting to hear whether the onboarding will show.
export const DECISION_TIMEOUT_MS = 10000;

// How long, once Intercom is running, we wait for it to report a message before loading the survey.
export const INTERCOM_GRACE_MS = 8000;

// The longest we wait for the Premium offer to finish, before carrying on without it.
export const PREMIUM_PROMO_WAIT_MS = 30000;

export const ONBOARDING = {
  PENDING: 'pending', // not decided yet
  SHOWING: 'showing', // on screen now
  FINISHED: 'finished', // was shown, and has closed
  NONE: 'none', // will not be shown in this visit
  // Still undecided when the time limit passed (a new reader reading the Premium page, say). Intercom
  // is let go, but the survey stays out: the onboarding may well still come.
  UNKNOWN: 'unknown',
};

// Set when the onboarding has been on screen for the logged-in reader, and kept for the browser
// session, so the survey also stays away after a page reload. Cleared when the reader logs out.
const SEEN_KEY = 'ckbk.onboarding.seenThisSession';

const readSeen = () => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage.getItem(SEEN_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const writeSeen = seen => {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    if (seen) {
      window.sessionStorage.setItem(SEEN_KEY, '1');
    } else {
      window.sessionStorage.removeItem(SEEN_KEY);
    }
  } catch (error) {
    // No sessionStorage: the survey then only stays away for this page load.
  }
};

const initialState = () => ({
  // The onboarding was shown to this reader earlier in the browser session (possibly before a reload).
  seenThisSession: readSeen(),
  // The Premium offer is about to be shown, or is on screen (the apps show it right after signing
  // in). It comes first, so everything else waits. This is not about a reader, so it survives a
  // change of reader.
  premiumPromoPending: false,
  // Counts the readers seen so far (each login or logout starts a new one).
  generation: 0,
  // Whether the decision below was made for a logged-in reader (true), a logged-out visitor
  // (false), or has not been made for anyone yet (null).
  forLoggedIn: null,
  onboarding: ONBOARDING.PENDING,
  // Intercom has been seen to show something (or to have an unread message).
  intercomMessage: false,
  // INTERCOM_GRACE_MS have passed since Intercom started, with the onboarding not shown.
  graceElapsed: false,
});

let state = initialState();
const listeners = new Set();
let decisionTimer = null;
let graceTimer = null;
let promoTimer = null;

const emit = () => listeners.forEach(listener => listener());

const set = changes => {
  state = { ...state, ...changes };
  emit();
};

const clearTimers = () => {
  clearTimeout(decisionTimer);
  clearTimeout(graceTimer);
  clearTimeout(promoTimer);
  decisionTimer = null;
  graceTimer = null;
  promoTimer = null;
};

// Intercom may start once the onboarding is closed or will not show, or we have waited long enough.
const settled = status =>
  status === ONBOARDING.FINISHED || status === ONBOARDING.NONE || status === ONBOARDING.UNKNOWN;

const startGraceIfNeeded = () => {
  if (state.onboarding !== ONBOARDING.NONE || graceTimer || state.graceElapsed) {
    return;
  }
  graceTimer = setTimeout(() => {
    graceTimer = null;
    set({ graceElapsed: true });
  }, INTERCOM_GRACE_MS);
};

// The decision timer only runs in a browser. It starts when something first listens, and again
// whenever a different reader arrives.
const startDecisionTimer = () => {
  if (
    decisionTimer ||
    typeof window === 'undefined' ||
    state.onboarding !== ONBOARDING.PENDING ||
    state.premiumPromoPending
  ) {
    return;
  }
  decisionTimer = setTimeout(() => {
    decisionTimer = null;
    // Still undecided: stop holding Intercom, but do not take that as "no onboarding".
    reportOnboarding(ONBOARDING.UNKNOWN, state.forLoggedIn !== false); // eslint-disable-line no-use-before-define
  }, DECISION_TIMEOUT_MS);
};

/**
 * Says who the reader is now: logged in or a logged-out visitor. Whenever that changes (the apps
 * log in and out without reloading the page) everything starts afresh, including the time limit on
 * holding Intercom, because what was decided for the last reader says nothing about this one.
 */
export function reportReader(isLoggedIn) {
  const loggedIn = !!isLoggedIn;
  if (!loggedIn) {
    writeSeen(false);
  }
  if (state.forLoggedIn === loggedIn) {
    return;
  }
  if (state.forLoggedIn === null) {
    state = { ...state, forLoggedIn: loggedIn, seenThisSession: loggedIn && readSeen() };
  } else {
    const { premiumPromoPending } = state;
    clearTimeout(decisionTimer);
    clearTimeout(graceTimer);
    decisionTimer = null;
    graceTimer = null;
    state = {
      ...initialState(),
      premiumPromoPending,
      generation: state.generation + 1,
      forLoggedIn: loggedIn,
    };
  }
  emit();
  startDecisionTimer();
}

/**
 * The onboarding says what it is going to do, for a logged-in reader or a logged-out visitor.
 * Only reportReader changes who the reader is: a report about anyone other than the current reader
 * (a late one, for the person who has just logged out) is ignored. The very first report also says
 * who the reader is.
 *
 * Moves forward only: pending -> showing | none | finished | unknown, unknown -> showing | none |
 * finished, showing -> finished. Anything else is ignored, so a late report cannot undo a decision
 * that others have already acted on.
 */
export function reportOnboarding(status, loggedIn = true) {
  if (state.forLoggedIn === null) {
    reportReader(loggedIn);
  } else if (state.forLoggedIn !== !!loggedIn) {
    return;
  }
  const { PENDING, SHOWING, NONE, FINISHED, UNKNOWN } = ONBOARDING;
  const allowed =
    (state.onboarding === PENDING && [SHOWING, NONE, FINISHED, UNKNOWN].includes(status)) ||
    (state.onboarding === UNKNOWN && [SHOWING, NONE, FINISHED].includes(status)) ||
    (state.onboarding === SHOWING && status === FINISHED);
  if (!allowed) {
    return;
  }
  if (settled(status)) {
    clearTimeout(decisionTimer);
    decisionTimer = null;
  }
  if (status === SHOWING) {
    writeSeen(true);
  }
  set({ onboarding: status, ...(status === SHOWING ? { seenThisSession: true } : {}) });
  startGraceIfNeeded();
}

// Intercom says it has shown, or has, a message.
export function reportIntercomMessage() {
  if (!state.intercomMessage) {
    set({ intercomMessage: true });
  }
}

/**
 * The Premium offer is about to be shown, or is on screen (true), or is done with (false). While it
 * is, the onboarding, Intercom and the survey wait for it. It always lets go after
 * PREMIUM_PROMO_WAIT_MS, so a Premium offer that never appears cannot hold anything for ever.
 */
export function setPremiumPromoPending(pending) {
  const value = !!pending;
  clearTimeout(promoTimer);
  promoTimer = null;
  if (value) {
    // Nothing to be decided while the Premium offer is still to come: stop the clock on holding
    // Intercom, and restart it when the offer is done.
    clearTimeout(decisionTimer);
    decisionTimer = null;
    if (typeof window !== 'undefined') {
      promoTimer = setTimeout(() => {
        promoTimer = null;
        setPremiumPromoPending(false);
      }, PREMIUM_PROMO_WAIT_MS);
    }
  }
  if (state.premiumPromoPending !== value) {
    set({ premiumPromoPending: value });
  }
  if (!value) {
    startDecisionTimer();
  }
}

/**
 * The onboarding was on screen but was pushed aside before the reader could really have seen it (the
 * Premium offer opened over it). It goes back to being undecided, so it can come back when it can.
 */
export function withdrawOnboarding(loggedIn = true) {
  if (state.forLoggedIn === !!loggedIn && state.onboarding === ONBOARDING.SHOWING) {
    writeSeen(false);
    set({ onboarding: ONBOARDING.PENDING, seenThisSession: false });
    startDecisionTimer();
  }
}

/**
 * The reader's ask history was rewritten from outside (the onboarding test links), so what was
 * decided about the onboarding in this visit no longer holds. It goes back to undecided, so it can
 * show if it is now due. Intercom and the survey may already have acted on the old decision.
 */
export function reopenOnboarding(loggedIn = true) {
  const { NONE, FINISHED, UNKNOWN } = ONBOARDING;
  if (state.forLoggedIn === !!loggedIn && [NONE, FINISHED, UNKNOWN].includes(state.onboarding)) {
    writeSeen(false);
    set({ onboarding: ONBOARDING.PENDING, seenThisSession: false });
    startDecisionTimer();
  }
}

export const getSnapshot = () => state;

export function subscribe(listener) {
  listeners.add(listener);
  startDecisionTimer();
  return () => listeners.delete(listener);
}

// Whether Intercom may start. A logged-out visitor has no onboarding to wait for. A logged-in reader
// waits until the onboarding has been settled *for them*, not for whoever was here before.
export const intercomMayStart = (snapshot, loggedIn = true) =>
  !loggedIn ||
  (snapshot.forLoggedIn === true && settled(snapshot.onboarding) && !snapshot.premiumPromoPending);

// Whether the NPS survey may be loaded: only when the onboarding was not shown, and Intercom
// had its few seconds to say it has a message and did not.
export const npsMayLoad = snapshot =>
  !snapshot.premiumPromoPending &&
  !snapshot.seenThisSession &&
  snapshot.forLoggedIn === true &&
  snapshot.onboarding === ONBOARDING.NONE &&
  snapshot.graceElapsed &&
  !snapshot.intercomMessage;

// For tests.
export function resetInterruptions() {
  clearTimers();
  writeSeen(false);
  state = initialState();
  emit();
}
